import { ShipmentReadBoundary } from "./shipment-read-boundary";
import { ReadFailureNotice } from "@/components/read-failure-notice";
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router";
import React from "react";
import { getOperatorDisplayName } from "@/lib/safe-access";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { useShipments, useShipmentLabels } from "@/api/shipments";
import { useCarrierScope } from "@/contexts/carrier-scope-context";
import {
  ShipmentStatus,
  type Shipment,
  type Address,
  type Task,
  TaskStatus,
  TaskType,
} from "@/api.types.v2";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ShipmentAddress {
  type: "FROM" | "TO";
  contactName?: string | null;
  contactCompany?: string | null;
  contactPhone?: string | null;
  address?: Address;
  instructions?: string | null;
}

interface ShipmentLabel {
  trackingNumber?: string;
  [key: string]: unknown;
}

// @ts-expect-error - Shipment extends with additional relational fields not in the base type
interface ShipmentWithLabels extends Shipment {
  labels?: ShipmentLabel[];
  addresses?: ShipmentAddress[];
  tasks?: Task[];
}
import { ShipmentDetailsDialog } from "./shipment-details-dialog";
import { ShipmentChangeRequestDialog } from "./shipment-change-request-dialog";
import { CancelShipmentDialog } from "./cancel-shipment-dialog";
import { formatDistanceToNow } from "date-fns";
import {
  Package,
  MoreHorizontal,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Copy,
  ExternalLink,
  Printer,
  Camera,
  CheckCircle2,
  ChevronDown,
  Clock,
  MapPin,
  User,
  FileText,
  AlertCircle,
  CheckCircle,
  Circle,
  XCircle,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useSession } from "@/services/auth0/session";
import { useQueryClient } from "@tanstack/react-query";

const statusColors: Record<ShipmentStatus, string> = {
  [ShipmentStatus.PENDING_PAYMENT]: "bg-amber-100 text-amber-800",
  [ShipmentStatus.LABEL_READY]: "bg-blue-100 text-blue-800",
  [ShipmentStatus.PICKED_UP]: "bg-purple-100 text-purple-800",
  [ShipmentStatus.IN_TRANSIT]: "bg-indigo-100 text-indigo-800",
  [ShipmentStatus.AT_FACILITY]: "bg-cyan-100 text-cyan-800",
  [ShipmentStatus.OUT_FOR_DELIVERY]: "bg-yellow-100 text-yellow-800",
  [ShipmentStatus.DELIVERED]: "bg-green-100 text-green-800",
  [ShipmentStatus.AVAILABLE_FOR_PICKUP]: "bg-teal-100 text-teal-800",
  [ShipmentStatus.DELIVERY_ATTEMPTED]: "bg-orange-100 text-orange-800",
  [ShipmentStatus.EXCEPTION]: "bg-red-200 text-red-900",
  [ShipmentStatus.ON_HOLD]: "bg-amber-100 text-amber-800",
  [ShipmentStatus.RETURNED]: "bg-brown-100 text-brown-800",
  [ShipmentStatus.CANCELLED]: "bg-red-100 text-red-800",
};

const taskStatusColors: Record<TaskStatus, string> = {
  [TaskStatus.PENDING]: "bg-gray-100 text-gray-700",
  [TaskStatus.IN_PROGRESS]: "bg-blue-100 text-blue-700",
  [TaskStatus.COMPLETED]: "bg-green-100 text-green-700",
  [TaskStatus.FAILED]: "bg-red-100 text-red-700",
  [TaskStatus.CANCELLED]: "bg-gray-100 text-gray-500",
};

const taskTypeLabels: Record<TaskType, string> = {
  [TaskType.CUSTOMER_PICKUP]: "Customer Pickup",
  [TaskType.CUSTOMER_DELIVERY]: "Customer Delivery",
  [TaskType.CUSTOMER_RETURN]: "Customer Return",
  [TaskType.CARRIER_DROPOFF]: "Carrier Dropoff",
  [TaskType.CARRIER_PICKUP]: "Carrier Pickup",
  [TaskType.DEPOT_RECEIVE]: "Dispatch Center Receive",
  [TaskType.DEPOT_SORT]: "Dispatch Center Sort",
  [TaskType.DEPOT_DISPATCH]: "Dispatch Center Dispatch",
  [TaskType.DEPOT_TRANSFER]: "Dispatch Center Transfer",
};

const getTaskStatusIcon = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.COMPLETED:
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    case TaskStatus.IN_PROGRESS:
      return <Clock className="h-4 w-4 text-blue-600" />;
    case TaskStatus.FAILED:
      return <XCircle className="h-4 w-4 text-red-600" />;
    case TaskStatus.CANCELLED:
      return <XCircle className="h-4 w-4 text-gray-400" />;
    default:
      return <Circle className="h-4 w-4 text-gray-400" />;
  }
};

// Timeline component for displaying tasks
export function TaskTimeline({ tasks }: { tasks: Task[] }) {
  // Sort tasks by createdAt timestamp
  const sortedTasks = [...tasks].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  if (sortedTasks.length === 0) {
    return (
      <div className="text-center text-gray-500 py-4">
        No tasks found for this shipment
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
        <Clock className="h-4 w-4" />
        Task Timeline ({sortedTasks.length} tasks)
      </h4>
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />

        <div className="space-y-4">
          {sortedTasks.map((task) => (
            <div key={task.id} className="relative pl-10">
              {/* Timeline dot */}
              <div className="absolute left-2 top-1 w-5 h-5 flex items-center justify-center bg-white">
                {getTaskStatusIcon(task.status)}
              </div>

              <div className="bg-white border rounded-lg p-3 shadow-xs">
                {/* Task header */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={taskStatusColors[task.status]}>
                      {task.status.replace(/_/g, " ")}
                    </Badge>
                    <span className="font-medium text-sm">
                      {taskTypeLabels[task.type] || task.type}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {format(new Date(task.createdAt), "MMM d, h:mm a")}
                  </span>
                </div>

                {/* Task details grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  {/* Operator */}
                  {task.operator && (
                    <div className="flex items-start gap-1.5">
                      <User className="h-3.5 w-3.5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-gray-500">Operator</p>
                        <p className="font-medium">
                          {getOperatorDisplayName(task.operator)}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Address */}
                  {task.address && (
                    <div className="flex items-start gap-1.5">
                      <MapPin className="h-3.5 w-3.5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-gray-500">Location</p>
                        <p className="font-medium">
                          {task.address.city}, {task.address.province}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Timing */}
                  {(task.startedAt || task.completedAt) && (
                    <div className="flex items-start gap-1.5">
                      <Clock className="h-3.5 w-3.5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-gray-500">Timing</p>
                        {task.startedAt && (
                          <p className="font-medium">
                            Started:{" "}
                            {format(new Date(task.startedAt), "MMM d, h:mm a")}
                          </p>
                        )}
                        {task.completedAt && (
                          <p className="font-medium">
                            Done:{" "}
                            {format(
                              new Date(task.completedAt),
                              "MMM d, h:mm a"
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Failure reason */}
                  {task.status === TaskStatus.FAILED && task.failureReason && (
                    <div className="flex items-start gap-1.5 col-span-2">
                      <AlertCircle className="h-3.5 w-3.5 text-red-400 mt-0.5" />
                      <div>
                        <p className="text-gray-500">Failure Reason</p>
                        <p className="font-medium text-red-600">
                          {task.failureReason}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Proof of completion */}
                {task.proof && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                      <FileText className="h-3.5 w-3.5" />
                      <span className="font-medium">Proof of Completion</span>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {task.proof.signature && (
                        <div className="flex items-center gap-1.5 text-xs">
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                          <span>
                            Signed
                            {task.proof.signedBy
                              ? ` by ${task.proof.signedBy}`
                              : ""}
                          </span>
                        </div>
                      )}
                      {task.proof.mediaItems &&
                        task.proof.mediaItems.length > 0 && (
                          <div className="flex items-center gap-2">
                            <Camera className="h-3.5 w-3.5 text-blue-600" />
                            <div className="flex gap-1">
                              {task.proof.mediaItems
                                .slice(0, 4)
                                .map((item, i) => (
                                  <img
                                    key={i}
                                    src={item.url}
                                    alt={`Proof ${i + 1}`}
                                    className="w-10 h-10 object-cover rounded border"
                                  />
                                ))}
                              {task.proof.mediaItems.length > 4 && (
                                <div className="w-10 h-10 bg-gray-100 rounded border flex items-center justify-center text-xs text-gray-500">
                                  +{task.proof.mediaItems.length - 4}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      {task.proof.notes && (
                        <p className="text-xs text-gray-600 w-full">
                          Note: {task.proof.notes}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Component to check label availability and render menu
function PrintLabelsMenu({
  shipmentId,
  shipment,
  onPrintLabels,
}: {
  shipmentId: string;
  shipment: Shipment | undefined;
  onPrintLabels: (
    shipmentId: string,
    format?: string,
    labelType?: "carrier" | "internal"
  ) => void;
}) {
  const { data: labels, refetch } = useShipmentLabels(shipmentId);
  const { token } = useSession();
  const queryClient = useQueryClient();
  const [isGenerating, setIsGenerating] = useState(false);

  // Check if carrier and internal labels exist. A self-rated carrier is one EZX
  // prices from its own rate tables, so its label is printed by EZX (internal);
  // anything procured through an integration ships on that provider's label.
  const hasCarrierLabels =
    labels?.some((label) => !label.carrier?.selfRated) ?? false;
  const hasInternalLabels =
    labels?.some((label) => label.carrier?.selfRated) ?? false;
  const hasAnyLabels = (labels?.length ?? 0) > 0;

  // Pricing-redesign: derive label-strategy from new model (no longer reads
  // shipment.serviceType). Equivalences:
  //   external carrier + pickupBookingId  ≡ legacy CARRIER_FULL_SERVICE
  //   external carrier + no booking       ≡ legacy CARRIER_LABEL_ONLY
  //   self-rated carrier                  ≡ legacy LOCAL_*
  const isExternalCarrier = shipment?.carrier?.selfRated === false;
  const hasPickupBooking = shipment?.pickupBookingId != null;
  const isSelfRatedCarrier = shipment?.carrier?.selfRated === true;

  // Carrier labels: any external carrier ships with a third-party label
  const shouldHaveCarrierLabels = isExternalCarrier;

  // Internal labels: self-rated for direct delivery, OR external-with-pickup
  // for the leg from sender to carrier dispatch center
  const shouldHaveInternalLabels =
    isSelfRatedCarrier || (isExternalCarrier && hasPickupBooking);

  // Check which formats are available for each type
  const carrierFormats = {
    PDF:
      labels?.some((l) => !l.carrier?.selfRated && l.labelFormat === "PDF") ??
      false,
    PNG:
      labels?.some((l) => !l.carrier?.selfRated && l.labelFormat === "PNG") ??
      false,
    ZPL:
      labels?.some((l) => !l.carrier?.selfRated && l.labelFormat === "ZPL") ??
      false,
  };

  const internalFormats = {
    PDF:
      labels?.some((l) => l.carrier?.selfRated && l.labelFormat === "PDF") ??
      false,
  };

  const handleGenerateLabels = async (labelType?: "internal" | "carrier") => {
    setIsGenerating(true);
    try {
      const url = new URL(
        `${import.meta.env.VITE_API_URL}/api/v2/shipping/shipments/${shipmentId}/generate-labels`
      );
      if (labelType) {
        url.searchParams.set("labelType", labelType);
      }

      const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { details?: string };
        throw new Error(errorData.details ?? "Failed to generate labels");
      }

      toast.success("Labels generated successfully");

      // Invalidate and refetch labels to get real-time updates
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["shipments"] }),
        queryClient.invalidateQueries({
          queryKey: ["shipment-labels", shipmentId],
        }),
        refetch(),
      ]);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to generate labels. Please try again."
      );
    } finally {
      setIsGenerating(false);
    }
  };

  // If no labels exist, show warning and generate button
  if (!hasAnyLabels) {
    return (
      <>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => handleGenerateLabels()}
          disabled={isGenerating}
          className="text-amber-600"
        >
          <Printer className="mr-2 h-4 w-4" />
          {isGenerating ? "Generating Labels..." : "⚠️ Generate Labels"}
        </DropdownMenuItem>
      </>
    );
  }

  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <Printer className="mr-2 h-4 w-4" />
          Print Labels
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              Carrier Labels{" "}
              {!hasCarrierLabels && (
                <span className="ml-auto text-xs text-gray-400">(none)</span>
              )}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {shouldHaveCarrierLabels && !hasCarrierLabels ? (
                <DropdownMenuItem
                  onClick={() => handleGenerateLabels("carrier")}
                  disabled={isGenerating}
                  className="text-amber-600"
                >
                  <Printer className="mr-2 h-4 w-4" />
                  {isGenerating ? "Generating..." : "⚠️ Generate Labels"}
                </DropdownMenuItem>
              ) : (
                <>
                  <DropdownMenuItem
                    disabled={!carrierFormats.PDF}
                    onClick={() => onPrintLabels(shipmentId, "PDF", "carrier")}
                    className="data-disabled:opacity-50 data-disabled:cursor-not-allowed"
                  >
                    PDF Format{" "}
                    {!carrierFormats.PDF && (
                      <span className="ml-auto text-xs text-gray-400">
                        (none)
                      </span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={!carrierFormats.PNG}
                    onClick={() => onPrintLabels(shipmentId, "PNG", "carrier")}
                    className="data-disabled:opacity-50 data-disabled:cursor-not-allowed"
                  >
                    PNG Format{" "}
                    {!carrierFormats.PNG && (
                      <span className="ml-auto text-xs text-gray-400">
                        (none)
                      </span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={!carrierFormats.ZPL}
                    onClick={() => onPrintLabels(shipmentId, "ZPL", "carrier")}
                    className="data-disabled:opacity-50 data-disabled:cursor-not-allowed"
                  >
                    ZPL Format (Thermal){" "}
                    {!carrierFormats.ZPL && (
                      <span className="ml-auto text-xs text-gray-400">
                        (none)
                      </span>
                    )}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              Internal Labels{" "}
              {!hasInternalLabels && (
                <span className="ml-auto text-xs text-gray-400">(none)</span>
              )}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {shouldHaveInternalLabels && !hasInternalLabels ? (
                <DropdownMenuItem
                  onClick={() => handleGenerateLabels("internal")}
                  disabled={isGenerating}
                  className="text-amber-600"
                >
                  <Printer className="mr-2 h-4 w-4" />
                  {isGenerating ? "Generating..." : "⚠️ Generate Labels"}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  disabled={!internalFormats.PDF}
                  onClick={() => onPrintLabels(shipmentId, "PDF", "internal")}
                  className="data-disabled:opacity-50 data-disabled:cursor-not-allowed"
                >
                  PDF Format{" "}
                  {!internalFormats.PDF && (
                    <span className="ml-auto text-xs text-gray-400">
                      (none)
                    </span>
                  )}
                </DropdownMenuItem>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    </>
  );
}

export function ShipmentsTable() {
  const { token, user } = useSession();
  const identity = token && user ? user.id : null;
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<ShipmentStatus | "ALL">(
    "ALL"
  );
  const { carrierIds: scopedCarrierIds } = useCarrierScope();
  const [search, setSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);
  const filterKey = JSON.stringify([
    identity,
    search,
    statusFilter,
    scopedCarrierIds,
  ]);
  const selectionKey = JSON.stringify([
    identity,
    searchTerm.trim(),
    statusFilter,
    scopedCarrierIds,
  ]);
  const [selection, setSelection] = useState({
    key: selectionKey,
    ids: new Set<string>(),
  });
  const selectedIds =
    selection.key === selectionKey ? selection.ids : new Set<string>();
  const [labelType, setLabelType] = useState<"carrier" | "internal">("carrier");
  const [printingBatch, setPrintingBatch] = useState(false);
  const [batchErrors, setBatchErrors] = useState<
    Array<{ shipmentId: string; shipmentNumber: number | null; reason: string }>
  >([]);
  const [batchSummary, setBatchSummary] = useState<{
    shipmentCount: number;
    labelCount: number;
    pageCount: number;
  } | null>(null);
  useEffect(() => {
    if (selection.key === selectionKey) return;
    if (selection.ids.size > 0) {
      toast.info("Shipment selection cleared because the list changed");
    }
    setSelection({ key: selectionKey, ids: new Set() });
    setBatchErrors([]);
    setBatchSummary(null);
  }, [selection, selectionKey]);
  const [pagination, setPagination] = useState({ key: filterKey, page: 1 });
  const page = pagination.key === filterKey ? pagination.page : 1;
  useEffect(() => {
    setPagination((current) =>
      current.key === filterKey ? current : { key: filterKey, page: 1 }
    );
  }, [filterKey]);
  const setPage = (next: number) =>
    setPagination({ key: filterKey, page: next });
  const limit = 10;
  const [selectedShipmentId, setSelectedShipmentId] = useState<string | null>(
    null
  );
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [changeRequestDialogOpen, setChangeRequestDialogOpen] = useState(false);
  const [
    selectedShipmentForChangeRequest,
    setSelectedShipmentForChangeRequest,
  ] = useState<Shipment | null>(null);
  const [expandedShipments, setExpandedShipments] = useState<Set<string>>(
    new Set()
  );
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [shipmentToCancel, setShipmentToCancel] = useState<Shipment | null>(
    null
  );

  // Toggle shipment expansion
  const toggleShipmentExpansion = (shipmentId: string) => {
    const newExpanded = new Set(expandedShipments);
    if (newExpanded.has(shipmentId)) {
      newExpanded.delete(shipmentId);
    } else {
      newExpanded.add(shipmentId);
    }
    setExpandedShipments(newExpanded);
  };

  const {
    data: shipmentsResponse,
    isPending,
    fetchStatus,
    isFetching,
    error,
    denied,
    isCoolingDown,
    refetch,
  } = useShipments({
    limit,
    offset: (page - 1) * limit,
    status: statusFilter !== "ALL" ? statusFilter : undefined,
    carrierIds: scopedCarrierIds.length ? scopedCarrierIds : undefined,
    search,
    include: ["tasks"],
  });

  const shipments = shipmentsResponse?.data ?? [];
  const total = shipmentsResponse?.metadata.total;
  const shownPage = shipmentsResponse
    ? Math.floor(
        shipmentsResponse.metadata.offset / shipmentsResponse.metadata.limit
      ) + 1
    : undefined;
  const totalPages =
    total === undefined || !shipmentsResponse
      ? undefined
      : Math.max(1, Math.ceil(total / shipmentsResponse.metadata.limit));
  const concealed = identity === null || denied;
  const readOnly =
    !shipmentsResponse ||
    !!error ||
    isFetching ||
    isPending ||
    isCoolingDown ||
    fetchStatus === "paused";
  const [displayRetry, setDisplayRetry] = useState(0);
  const [dialogOwner, setDialogOwner] = useState(identity);
  useEffect(() => {
    if (dialogOwner === identity && !denied) return;
    setDetailsDialogOpen(false);
    setSelectedShipmentId(null);
    setChangeRequestDialogOpen(false);
    setSelectedShipmentForChangeRequest(null);
    setCancelDialogOpen(false);
    setShipmentToCancel(null);
    setExpandedShipments(new Set());
    setSelection({ key: selectionKey, ids: new Set() });
    setBatchErrors([]);
    setBatchSummary(null);
    setDialogOwner(identity);
  }, [identity, denied, dialogOwner, selectionKey]);
  const pageIds = shipments.map((shipment) => shipment.id);
  const selectedOnPage = pageIds.filter((id) => selectedIds.has(id)).length;
  const selectedOffPage = selectedIds.size - selectedOnPage;
  const updateSelection = (ids: string[], checked: boolean) => {
    setSelection((current) => {
      const next = new Set(current.key === selectionKey ? current.ids : []);
      for (const id of ids) checked ? next.add(id) : next.delete(id);
      return { key: selectionKey, ids: next };
    });
    setBatchErrors([]);
    setBatchSummary(null);
  };

  const handleBatchPrint = async () => {
    if (selectedIds.size === 0 || printingBatch || readOnly) return;
    const preview = window.open("", "_blank");
    if (!preview) {
      toast.error("Allow pop-ups to open the printable labels");
      return;
    }
    preview.document.title = "Preparing labels";
    preview.document.body.textContent = "Preparing selected labels...";
    setPrintingBatch(true);
    setBatchErrors([]);
    setBatchSummary(null);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v2/shipping/shipments/labels/batch-download`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            shipmentIds: [...selectedIds],
            labelType,
          }),
        }
      );
      if (!response.ok) {
        preview.close();
        const payload = (await response.json()) as {
          error?: string;
          shipments?: Array<{
            shipmentId: string;
            shipmentNumber: number | null;
            reason: string;
          }>;
        };
        setBatchErrors(payload.shipments ?? []);
        toast.error(payload.error ?? "Failed to prepare selected labels");
        return;
      }
      const url = URL.createObjectURL(await response.blob());
      setBatchSummary({
        shipmentCount: selectedIds.size,
        labelCount: Number(response.headers.get("X-Label-Count") ?? 0),
        pageCount: Number(response.headers.get("X-Page-Count") ?? 0),
      });
      preview.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      preview.close();
      toast.error("Failed to prepare selected labels");
    } finally {
      setPrintingBatch(false);
    }
  };

  const handleCopyTracking = (trackingNumber: string) => {
    void navigator.clipboard.writeText(trackingNumber);
    toast.success("Copied", {
      description: "Tracking number copied to clipboard",
    });
  };

  const handlePrintLabels = async (
    shipmentId: string,
    format?: string,
    labelType?: "carrier" | "internal"
  ) => {
    try {
      // Build download URL with query parameters
      const url = new URL(
        `${import.meta.env.VITE_API_URL}/api/v2/shipping/shipments/${shipmentId}/labels/download`
      );
      if (format) {
        url.searchParams.set("format", format);
      }
      if (labelType) {
        url.searchParams.set("labelType", labelType);
      }

      console.log("🖨️ Fetching merged labels:", {
        shipmentId,
        format: format || "all",
        labelType: labelType || "all",
      });

      // Fetch the PDF with authentication
      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch labels: ${response.statusText}`);
      }

      // Create blob URL and open in new tab
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");

      // Keep the URL usable for the PDF viewer's later Save action.
      // The browser releases it when this document unloads.
    } catch (error) {
      console.error("❌ Error opening labels:", error);
      toast.error("Failed to open labels");
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
    }).format(cents / 100);
  };

  const getShipmentAddresses = (shipment: ShipmentWithLabels) => {
    // Pattern B (task #258): role-tagged addresses[] is the source of truth.
    // The dual-shape handler that preferred shipment.shipFrom/.shipTo was
    // legacy — those flat fields aren't consistently populated and the
    // addresses[] branch was already a fallback, so promoting it primary
    // keeps the read shape consistent across endpoints.
    const fromAddressData = shipment.addresses?.find((a) => a.type === "FROM");
    const toAddressData = shipment.addresses?.find((a) => a.type === "TO");

    return {
      fromAddress: fromAddressData
        ? {
            name: fromAddressData.contactName,
            company: fromAddressData.contactCompany,
            phone: fromAddressData.contactPhone,
            address: fromAddressData.address,
            instructions: fromAddressData.instructions,
          }
        : null,
      toAddress: toAddressData
        ? {
            name: toAddressData.contactName,
            company: toAddressData.contactCompany,
            phone: toAddressData.contactPhone,
            address: toAddressData.address,
            instructions: toAddressData.instructions,
          }
        : null,
    };
  };

  // Check if shipment has POD (proof of delivery)
  const getProofOfDelivery = (shipment: ShipmentWithLabels) => {
    // Look for completed delivery tasks with proof
    // Only CUSTOMER_DELIVERY and CARRIER_DROPOFF exist in current schema
    const deliveryTasks = shipment.tasks?.filter(
      (t) =>
        (t.type === TaskType.CUSTOMER_DELIVERY ||
          t.type === TaskType.CARRIER_DROPOFF) &&
        t.status === TaskStatus.COMPLETED
    );

    if (!deliveryTasks?.length) return null;

    // Check if any delivery task has proof
    const taskWithProof = deliveryTasks.find((t) => t.proof);
    if (!taskWithProof?.proof) return null;

    return {
      hasSignature: !!taskWithProof.proof.signature,
      hasPhotos: (taskWithProof.proof.mediaItems?.length ?? 0) > 0,
      signedBy: taskWithProof.proof.signedBy,
      notes: taskWithProof.proof.notes,
    };
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Tracking, order, contact, address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(value) =>
            setStatusFilter(value as ShipmentStatus | "ALL")
          }
        >
          <SelectTrigger className="w-[180px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Statuses</SelectItem>
            {Object.values(ShipmentStatus).map((status) => (
              <SelectItem key={status} value={status}>
                {status.replace(/_/g, " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon">
          <Download className="h-4 w-4" />
        </Button>
      </div>

      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/30 p-3">
          <span className="text-sm font-medium">
            {selectedIds.size}{" "}
            {selectedIds.size === 1 ? "shipment" : "shipments"} selected
            {selectedOffPage > 0 ? ` · ${selectedOffPage} on other pages` : ""}
          </span>
          <Select
            value={labelType}
            onValueChange={(value) =>
              setLabelType(value as "carrier" | "internal")
            }
          >
            <SelectTrigger className="w-[180px]" aria-label="Label type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="carrier">Carrier labels</SelectItem>
              <SelectItem value="internal">TooEZ internal labels</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            onClick={() => void handleBatchPrint()}
            disabled={printingBatch || readOnly || selectedIds.size > 100}
          >
            {printingBatch ? "Preparing labels..." : "Print selected"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelection({ key: selectionKey, ids: new Set() });
              setBatchErrors([]);
              setBatchSummary(null);
            }}
          >
            Clear
          </Button>
          {selectedIds.size > 100 && (
            <span className="text-sm text-destructive">
              Print up to 100 shipments at a time.
            </span>
          )}
        </div>
      )}
      {batchErrors.length > 0 && (
        <div
          role="alert"
          className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm"
        >
          <p className="font-semibold">
            No labels were printed. Resolve these shipments and retry:
          </p>
          <ul className="list-disc pl-5">
            {batchErrors.map((item) => (
              <li key={`${item.shipmentId}-${item.reason}`}>
                {item.shipmentNumber === null
                  ? item.shipmentId
                  : `SHP-${item.shipmentNumber}`}
                : {item.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
      {batchSummary && (
        <p role="status" className="text-sm text-muted-foreground">
          {batchSummary.shipmentCount}{" "}
          {batchSummary.shipmentCount === 1 ? "shipment" : "shipments"} selected
          · {batchSummary.labelCount} labels ready · {batchSummary.pageCount}{" "}
          PDF pages
        </p>
      )}

      {/* Table */}
      {(!!error || isCoolingDown) && (
        <ReadFailureNotice
          error={error}
          resource="shipments"
          onRetry={() => void refetch()}
          isRetrying={isFetching || fetchStatus === "paused"}
          isCoolingDown={isCoolingDown}
          retained={
            shipmentsResponse
              ? `Showing the last confirmed page (${shownPage}).${shownPage !== page ? ` Requested page ${page} has not loaded.` : ""}`
              : undefined
          }
        />
      )}
      {shipmentsResponse && (isFetching || fetchStatus === "paused") && (
        <p role="status">
          {fetchStatus === "paused"
            ? `Waiting for a connection to load requested page ${page}. Showing confirmed page ${shownPage}.`
            : `Loading requested page ${page}. Showing confirmed page ${shownPage}.`}
        </p>
      )}
      <div className="rounded-md border">
        <ShipmentReadBoundary
          key={displayRetry}
          resetKey={shipmentsResponse}
          isReloading={isFetching || fetchStatus === "paused"}
          isCoolingDown={isCoolingDown}
          onReload={async () => {
            const refreshed = await refetch();
            if (refreshed?.isSuccess) setDisplayRetry((value) => value + 1);
          }}
          renderDisplay={() => (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      aria-label="Select shipments on this page"
                      checked={
                        selectedOnPage === 0
                          ? false
                          : selectedOnPage === pageIds.length
                            ? true
                            : "indeterminate"
                      }
                      disabled={pageIds.length === 0}
                      onCheckedChange={(checked) =>
                        updateSelection(pageIds, checked === true)
                      }
                    />
                  </TableHead>
                  <TableHead>Shipment Number</TableHead>
                  <TableHead>Tracking Number</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Carrier</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead className="text-center">POD</TableHead>
                  <TableHead>Packages</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!shipmentsResponse && (isPending || identity === null) ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8">
                      {identity === null
                        ? "Confirming shipment access…"
                        : fetchStatus === "paused"
                          ? "Waiting for a connection to load shipments."
                          : "Loading shipments..."}
                    </TableCell>
                  </TableRow>
                ) : error && !shipmentsResponse ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8">
                      No confirmed shipment results available.
                    </TableCell>
                  </TableRow>
                ) : shipmentsResponse && shipments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8">
                      No shipments found
                    </TableCell>
                  </TableRow>
                ) : (
                  shipments.map((shipment) => {
                    const { fromAddress, toAddress } = getShipmentAddresses(
                      shipment as unknown as ShipmentWithLabels
                    );
                    // Calculate total cost from quotes if available
                    // Get the cost from sellAmountCents or quotes
                    let totalCost = shipment.sellAmountCents ?? 0;

                    // If no sellAmountCents but we have quotes, use the first quote
                    if (
                      !totalCost &&
                      shipment.quotes &&
                      shipment.quotes.length > 0
                    ) {
                      // Get the selected quote or first quote's retail rate
                      // Assuming retailRate is in dollars, convert to cents
                      const selectedQuote = shipment.quotes[0];
                      totalCost = Math.round(
                        (selectedQuote.retailRate || 0) * 100
                      );
                    }

                    return (
                      <React.Fragment key={shipment.id}>
                        <TableRow
                          className="hover:bg-muted/50 cursor-pointer"
                          onClick={() => toggleShipmentExpansion(shipment.id)}
                        >
                          <TableCell
                            onClick={(event) => event.stopPropagation()}
                          >
                            <Checkbox
                              disabled={readOnly}
                              aria-label={`Select SHP-${shipment.shipmentNumber}`}
                              checked={selectedIds.has(shipment.id)}
                              onCheckedChange={(checked) =>
                                updateSelection([shipment.id], checked === true)
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <ChevronDown
                                className={`h-4 w-4 text-gray-400 transition-transform ${
                                  expandedShipments.has(shipment.id)
                                    ? "rotate-180"
                                    : ""
                                }`}
                              />
                              <div>
                                <p className="font-medium">
                                  SHP-{shipment.shipmentNumber}
                                </p>
                                {shipment.order?.orderNumber && (
                                  <p className="text-xs text-gray-400">
                                    ORD-{shipment.order.orderNumber}
                                  </p>
                                )}
                                {shipment.deliveryNotes?.trim() && (
                                  <p
                                    className="max-w-52 truncate text-xs text-amber-800"
                                    title={shipment.deliveryNotes}
                                  >
                                    Delivery instructions:{" "}
                                    {shipment.deliveryNotes
                                      .trim()
                                      .replace(/\s+/g, " ")}
                                  </p>
                                )}
                                {fromAddress?.instructions?.trim() && (
                                  <p
                                    className="max-w-52 truncate text-xs text-amber-800"
                                    title={fromAddress.instructions}
                                  >
                                    Sender pickup instructions:{" "}
                                    {fromAddress.instructions
                                      .trim()
                                      .replace(/\s+/g, " ")}
                                  </p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              {shipment.trackingNumber && (
                                <div>
                                  <div className="flex items-center gap-1">
                                    <Link
                                      to={`/tracking/${shipment.trackingNumber}`}
                                      className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {shipment.trackingNumber}
                                    </Link>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-4 w-4"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopyTracking(
                                          shipment.trackingNumber ?? ""
                                        );
                                      }}
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  {/* Show carrier tracking numbers from labels */}
                                  {(shipment as ShipmentWithLabels).labels &&
                                    (() => {
                                      // Get unique carrier tracking numbers (filter out voided and group by tracking number)
                                      const carrierTrackingNumbers = Array.from(
                                        new Set(
                                          (
                                            shipment as ShipmentWithLabels
                                          ).labels
                                            ?.filter(
                                              (label) =>
                                                label.trackingNumber &&
                                                label.trackingNumber !==
                                                  shipment.trackingNumber
                                            )
                                            .map(
                                              (label) => label.trackingNumber
                                            )
                                        )
                                      );

                                      return carrierTrackingNumbers.length >
                                        0 ? (
                                        <div className="mt-1">
                                          {carrierTrackingNumbers.map(
                                            (tracking, idx) => (
                                              <div
                                                key={idx}
                                                className="flex items-center gap-1"
                                              >
                                                <p className="text-xs text-gray-600">
                                                  Carrier: {String(tracking)}
                                                </p>
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-3 w-3"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCopyTracking(
                                                      String(tracking)
                                                    );
                                                  }}
                                                >
                                                  <Copy className="h-2 w-2" />
                                                </Button>
                                              </div>
                                            )
                                          )}
                                        </div>
                                      ) : null;
                                    })()}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Badge
                                className={statusColors[shipment.status]}
                                variant="secondary"
                              >
                                {shipment.status.replace(/_/g, " ")}
                              </Badge>
                              {shipment.status === ShipmentStatus.EXCEPTION && (
                                <span className="text-xs text-red-600 font-medium">
                                  ⚠️ Needs Attention
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {shipment.carrier && (
                                <Badge variant="outline">
                                  {shipment.carrier.name ||
                                    shipment.carrier.code}
                                </Badge>
                              )}
                              {shipment.pickupBookingId ? (
                                <span className="text-xs text-gray-500">
                                  Pickup attached
                                </span>
                              ) : (
                                <span className="text-xs text-gray-500">
                                  Drop-off
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm space-y-0.5">
                              {fromAddress?.name && (
                                <p className="font-medium">
                                  {fromAddress.name}
                                </p>
                              )}
                              {fromAddress?.company && (
                                <p className="text-xs">{fromAddress.company}</p>
                              )}
                              {fromAddress?.phone && (
                                <p className="text-xs text-gray-600">
                                  {fromAddress.phone}
                                </p>
                              )}
                              {fromAddress?.address && (
                                <p className="text-xs text-gray-500">
                                  {fromAddress.address.city},{" "}
                                  {fromAddress.address.province ||
                                    (fromAddress.address as { state?: string })
                                      .state}
                                </p>
                              )}
                              {!fromAddress?.name && !fromAddress?.address && (
                                <span className="text-gray-400">—</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm space-y-0.5">
                              {toAddress?.name && (
                                <p className="font-medium">{toAddress.name}</p>
                              )}
                              {toAddress?.company && (
                                <p className="text-xs">{toAddress.company}</p>
                              )}
                              {toAddress?.phone && (
                                <p className="text-xs text-gray-600">
                                  {toAddress.phone}
                                </p>
                              )}
                              {toAddress?.address && (
                                <p className="text-xs text-gray-500">
                                  {toAddress.address.city},{" "}
                                  {toAddress.address.province ||
                                    (toAddress.address as { state?: string })
                                      .state}
                                </p>
                              )}
                              {!toAddress?.name && !toAddress?.address && (
                                <span className="text-gray-400">—</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {(() => {
                              const pod = getProofOfDelivery(
                                shipment as unknown as ShipmentWithLabels
                              );
                              if (!pod)
                                return <span className="text-gray-300">—</span>;
                              return (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <div className="flex items-center justify-center gap-1">
                                        {pod.hasSignature && (
                                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                                        )}
                                        {pod.hasPhotos && (
                                          <Camera className="h-4 w-4 text-blue-600" />
                                        )}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <div className="text-xs">
                                        {pod.hasSignature && (
                                          <p>
                                            ✓ Signed
                                            {pod.signedBy
                                              ? ` by ${pod.signedBy}`
                                              : ""}
                                          </p>
                                        )}
                                        {pod.hasPhotos && (
                                          <p>✓ Photos captured</p>
                                        )}
                                        {pod.notes && (
                                          <p className="text-gray-400 mt-1">
                                            {pod.notes}
                                          </p>
                                        )}
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Package className="h-4 w-4 text-gray-400" />
                              <span>{shipment.packages?.length ?? 0}</span>
                            </div>
                            {shipment.totalWeight && (
                              <p className="text-xs text-gray-500">
                                {(shipment.totalWeight / 1000).toFixed(2)} kg
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            <div>
                              {totalCost > 0 && (
                                <>
                                  <p className="font-medium">
                                    {formatCurrency(totalCost)}
                                  </p>
                                  {shipment.quotes &&
                                    shipment.quotes.length > 1 && (
                                      <p className="text-xs text-gray-500">
                                        {shipment.quotes.length} quotes
                                      </p>
                                    )}
                                </>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p>
                                {formatDistanceToNow(
                                  new Date(shipment.createdAt)
                                )}{" "}
                                ago
                              </p>
                              {shipment.estimatedDelivery && (
                                <p className="text-xs text-gray-500">
                                  ETA:{" "}
                                  {new Date(
                                    shipment.estimatedDelivery
                                  ).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell
                            className="text-right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={readOnly}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setSelectedShipmentId(shipment.id);
                                    setDetailsDialogOpen(true);
                                  }}
                                >
                                  <Eye className="mr-2 h-4 w-4" />
                                  View Details
                                </DropdownMenuItem>
                                {shipment.trackingNumber && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      navigate(
                                        `/tracking/${shipment.trackingNumber}`
                                      )
                                    }
                                  >
                                    <Package className="mr-2 h-4 w-4" />
                                    View Tracking
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => {
                                    setSelectedShipmentForChangeRequest(
                                      shipment
                                    );
                                    setChangeRequestDialogOpen(true);
                                  }}
                                >
                                  <Edit className="mr-2 h-4 w-4" />
                                  Request Changes
                                </DropdownMenuItem>
                                {shipment.trackingUrl && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      window.open(
                                        shipment.trackingUrl,
                                        "_blank"
                                      )
                                    }
                                  >
                                    <ExternalLink className="mr-2 h-4 w-4" />
                                    Track Package
                                  </DropdownMenuItem>
                                )}
                                {shipment.status !==
                                  ShipmentStatus.PENDING_PAYMENT && (
                                  <PrintLabelsMenu
                                    shipmentId={shipment.id}
                                    shipment={shipment}
                                    onPrintLabels={handlePrintLabels}
                                  />
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={() => {
                                    setShipmentToCancel(shipment);
                                    setCancelDialogOpen(true);
                                  }}
                                >
                                  Cancel Shipment
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                        {/* Expanded row with task timeline */}
                        {expandedShipments.has(shipment.id) && (
                          <TableRow>
                            <TableCell colSpan={12} className="bg-muted/30 p-0">
                              <div className="p-4">
                                <TaskTimeline
                                  tasks={
                                    (shipment as unknown as ShipmentWithLabels)
                                      .tasks ?? []
                                  }
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        />
      </div>

      {/* Pagination */}
      {totalPages !== undefined && totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-500">
            Page {shownPage} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={
                page === 1 ||
                isFetching ||
                isPending ||
                fetchStatus === "paused" ||
                isCoolingDown
              }
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={
                page >= totalPages ||
                isFetching ||
                isPending ||
                fetchStatus === "paused" ||
                isCoolingDown ||
                !!error
              }
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {!concealed && dialogOwner === identity && (
        <React.Fragment key={identity}>
          {/* Shipment Details Dialog */}
          <ShipmentDetailsDialog
            shipmentId={selectedShipmentId}
            open={detailsDialogOpen}
            onOpenChange={setDetailsDialogOpen}
          />

          {/* Shipment Change Request Dialog */}
          {selectedShipmentForChangeRequest && (
            <ShipmentChangeRequestDialog
              open={changeRequestDialogOpen}
              onOpenChange={setChangeRequestDialogOpen}
              shipment={selectedShipmentForChangeRequest}
            />
          )}

          {/* Cancel Shipment Dialog */}
          <CancelShipmentDialog
            open={cancelDialogOpen}
            onOpenChange={setCancelDialogOpen}
            shipment={shipmentToCancel}
          />
        </React.Fragment>
      )}
    </div>
  );
}
